import { useState } from 'react';
import ImageCard from '@components/common/ImageCard';
import { DATA_BASE_URL } from '@lib/constants';
import Icon from '@components/common/Icon';

const videos = [
  {
    id: 1,
    title: 'FRC Sydney 2025 Competition',
    description: 'Highlights from FRC Sydney 2025 competition with Team Tsinelas',
    src: `${DATA_BASE_URL}/data/robotics/videos/frc_sydney_2025.mp4`,
    poster: `${DATA_BASE_URL}/data/robotics/images/frc_sydney_robot_2024.webp`,
    type: 'FRC',
  },
  {
    id: 2,
    title: 'FRC Taipei Final 2025',
    description: 'Final match footage from FRC Taipei Regional 2025',
    src: `${DATA_BASE_URL}/data/robotics/videos/frc_taipei_final_2025.mp4`,
    poster: `${DATA_BASE_URL}/data/robotics/images/frc_taipei_robot_2025.webp`,
    type: 'FRC',
  },
  {
    id: 3,
    title: 'Competition Highlights',
    description: 'Best moments from various robotics competitions',
    src: `${DATA_BASE_URL}/data/robotics/videos/competition_video_1.mp4`,
    poster: `${DATA_BASE_URL}/data/robotics/images/vex_worlds_2022.webp`,
    type: 'VEX',
  },
];

const competitions = [
  {
    name: 'VEX Worlds Dallas 2025',
    location: 'Dallas, Texas',
    date: 'April 2025',
    description: 'Competed at the VEX Robotics World Championship, representing the Philippines on the global stage.',
    achievement: 'World Championship Qualification',
    images: [
      `${DATA_BASE_URL}/data/robotics/images/vex_taipei_2025_1.webp`,
      `${DATA_BASE_URL}/data/robotics/images/vex_taipei_2025_2.webp`,
      `${DATA_BASE_URL}/data/robotics/images/vex_taipei_2025_3.webp`
    ]
  },
  {
    name: 'VEX Dallas 2025',
    location: 'Dallas, Texas',
    date: 'March 2025',
    description: 'International competition showcasing advanced autonomous systems and strategic gameplay.',
    achievement: 'Tournament Excellence Award',
    images: [
      `${DATA_BASE_URL}/data/robotics/images/vex_dallas_2025_1.webp`,
      `${DATA_BASE_URL}/data/robotics/images/vex_dallas_2025_2.webp`,
      `${DATA_BASE_URL}/data/robotics/images/vex_dallas_team_2025.webp`
    ]
  },
  {
    name: 'VEX Worlds Dallas 2023',
    location: 'Dallas, Texas',
    date: 'April 2023',
    description: 'Competed at the VEX Robotics World Championship, representing the Philippines on the global stage.',
    achievement: 'World Championship Qualification',
    images: [
      `${DATA_BASE_URL}/data/robotics/images/vex_dallas_2025_1.webp`,
      `${DATA_BASE_URL}/data/robotics/images/vex_dallas_2025_2.webp`,
      `${DATA_BASE_URL}/data/robotics/images/vex_dallas_team_2025.webp`
    ]
  },
  {
    name: 'VEX Philippines Nationals 2023, 2024, 2025',
    location: 'Manila, Philippines',
    date: '2023, 2024, 2025',
    description: 'National championships where my team secured Excellence Awards and qualified for Worlds multiple times.',
    achievement: 'Excellence Awards & National Champions',
    images: [
      `${DATA_BASE_URL}/data/robotics/images/vex_excellence_award_philippines_nationals_2024.webp`,
      `${DATA_BASE_URL}/data/robotics/images/vex_philippines_nationals_2024.webp`,
      `${DATA_BASE_URL}/data/robotics/images/vex_philippines_national_award_2024.webp`,
      `${DATA_BASE_URL}/data/robotics/images/vex_robot_design_nationals_2024.webp`
    ]
  },
  {
    name: 'FRC Sydney Regional 2024',
    location: 'Sydney, Australia',
    date: 'March 2024',
    description: 'Founding tournament for the Philippines\' first FRC team, earning the prestigious Rookie Inspiration Award.',
    achievement: 'Rookie Inspiration Award',
    images: [
      `${DATA_BASE_URL}/data/robotics/images/frc_sydney_robot_2024.webp`,
      `${DATA_BASE_URL}/data/robotics/images/frc_sydney_competition_2024_1.webp`,
      `${DATA_BASE_URL}/data/robotics/images/frc_sydney_competition_2024_2.webp`,
      `${DATA_BASE_URL}/data/robotics/images/frc_sydney_rookie_inspiration_award_2024.webp`
    ]
  },
  {
    name: 'FRC Taipei Regional 2025',
    location: 'Taipei, Taiwan',
    date: 'February 2025',
    description: 'Advanced to the finals with improved autonomous systems and strategic alliance play.',
    achievement: 'Regional Finalist',
    images: [
      `${DATA_BASE_URL}/data/robotics/images/frc_taipei_finalist_2025.webp`,
      `${DATA_BASE_URL}/data/robotics/images/frc_taipei_robot_2025.webp`,
      `${DATA_BASE_URL}/data/robotics/images/frc_taipei_pit_area_2025.webp`
    ]
  }
];

const CompetitionShowcaseSection = () => {
  const [activeCompetition, setActiveCompetition] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);

  return (
    <section
      className="py-16 sm:py-20 px-4"
      style={{ backgroundColor: 'var(--color-theme-1)' }}
    >
      <div className="container mx-auto max-w-7xl">
        {/* ===== VIDEOS SECTION ===== */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Icon name="robotics" size="xl" style={{ color: 'var(--color-accent-6)' }} />
              <h2
                className="text-3xl sm:text-4xl font-heading font-bold tracking-tight"
                style={{ color: 'var(--color-heading)' }}
              >
                Competition Pictures and Videos
              </h2>
            </div>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: 'var(--color-subtle)' }}
            >
              Watch action-packed moments from FRC and VEX competitions
            </p>
          </div>

          {/* Video Grid - Horizontal scroll on mobile, grid on desktop */}
          <div className="md:grid md:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto gap-6 pb-4 snap-x snap-mandatory scrollbar-hide">
            {videos.map((video) => (
              <div
                key={video.id}
                onClick={() => setSelectedVideo(video)}
                className="group cursor-pointer rounded-xl overflow-hidden shadow-card transition-all duration-300 hover:shadow-card-hover flex-shrink-0 w-[85vw] md:w-auto snap-center"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  borderColor: 'var(--color-card-border)',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                }}
              >
                {/* Video Thumbnail */}
                <div className="relative aspect-video overflow-hidden bg-black">
                  <img
                    src={video.poster}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div
                    className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    }}
                  >
                    <svg
                      className="w-16 h-16"
                      fill="white"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>

                  {/* Type Badge */}
                  <div
                    className="absolute top-3 right-3 px-3 py-1 rounded-full text-sm font-semibold"
                    style={{
                      backgroundColor: 'var(--color-accent-6)',
                      color: 'var(--color-body-bg)',
                    }}
                  >
                    {video.type}
                  </div>
                </div>

                {/* Video Info */}
                <div className="p-6 space-y-3">
                  <h3
                    className="text-xl font-semibold"
                    style={{ color: 'var(--color-heading)' }}
                  >
                    {video.title}
                  </h3>
                  <p
                    className="text-sm"
                    style={{ color: 'var(--color-subtle)' }}
                  >
                    {video.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Featured Video Player Modal */}
          {selectedVideo && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
              }}
              onClick={() => setSelectedVideo(null)}
            >
              <div
                className="w-full max-w-5xl rounded-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close Button */}
                <button
                  onClick={() => setSelectedVideo(null)}
                  className="absolute top-4 right-4 z-10 p-2 rounded-full transition-colors duration-200"
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                  }}
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {/* Video Player */}
                <video
                  controls
                  autoPlay
                  className="w-full aspect-video"
                  poster={selectedVideo.poster}
                >
                  <source
                    src={selectedVideo.src}
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>

                {/* Video Title */}
                <div
                  className="p-6 space-y-2"
                  style={{
                    backgroundColor: 'var(--color-card-bg)',
                  }}
                >
                  <h3
                    className="text-2xl font-bold"
                    style={{ color: 'var(--color-heading)' }}
                  >
                    {selectedVideo.title}
                  </h3>
                  <p
                    className="text-lg"
                    style={{ color: 'var(--color-subtle)' }}
                  >
                    {selectedVideo.description}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ===== COMPETITIONS SECTION ===== */}
        <div>
          <div className="text-center mb-12">
            <h2
              className="text-3xl sm:text-4xl font-heading font-bold tracking-tight"
              style={{ color: 'var(--color-heading)' }}
            >
              Top 5 Competitions
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto mt-4"
              style={{ color: 'var(--color-subtle)' }}
            >
              Explore our tournament achievements with photos and details
            </p>
          </div>

          {/* Competition Tabs - Horizontal scroll on mobile */}
          <div className="overflow-x-auto pb-4 mb-8 sm:mb-12 scrollbar-hide">
            <div className="flex justify-center gap-3 min-w-max px-4 sm:px-0">
              {competitions.map((comp, index) => (
                <button
                  key={index}
                  onClick={() => setActiveCompetition(index)}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap"
                  style={{
                    backgroundColor: activeCompetition === index ? 'var(--color-accent-6)' : 'var(--color-card-bg)',
                    color: activeCompetition === index ? 'white' : 'var(--color-card-text)',
                    borderColor: activeCompetition === index ? 'var(--color-accent-6)' : 'var(--color-border-light)',
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                >
                  {comp.name}
                </button>
              ))}
            </div>
          </div>

          {/* Active Competition Display */}
          <div
            className="rounded-xl p-8 shadow-card"
            style={{
              backgroundColor: 'var(--color-card-bg)',
              borderColor: 'var(--color-border-light)',
              borderWidth: '1px',
              borderStyle: 'solid'
            }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              {/* Competition Info */}
              <div className="space-y-6">
                <div>
                  <div
                    className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3"
                    style={{
                      backgroundColor: 'var(--color-accent-1)',
                      color: 'var(--color-accent-7)'
                    }}
                  >
                    {competitions[activeCompetition].date}
                  </div>
                  <h3
                    className="text-2xl sm:text-3xl font-bold mb-2"
                    style={{ color: 'var(--color-heading)' }}
                  >
                    {competitions[activeCompetition].name}
                  </h3>
                  <p
                    className="text-base font-medium mb-4"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {competitions[activeCompetition].location}
                  </p>
                </div>

                <p
                  className="text-base leading-relaxed"
                  style={{ color: 'var(--color-card-text)' }}
                >
                  {competitions[activeCompetition].description}
                </p>

                <div
                  className="rounded-lg p-4"
                  style={{
                    backgroundColor: 'var(--color-theme-2)',
                    borderLeftWidth: '4px',
                    borderLeftStyle: 'solid',
                    borderLeftColor: 'var(--color-accent-6)'
                  }}
                >
                  <div
                    className="text-xs font-semibold uppercase tracking-wide mb-1"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    Achievement
                  </div>
                  <div
                    className="text-lg font-bold"
                    style={{ color: 'var(--color-accent-7)' }}
                  >
                    {competitions[activeCompetition].achievement}
                  </div>
                </div>
              </div>

              {/* Image Gallery - Horizontal scroll on mobile, grid on desktop */}
              <div className="md:grid md:grid-cols-2 md:gap-4 flex md:flex-none overflow-x-auto gap-4 pb-4 snap-x snap-mandatory scrollbar-hide">
                {competitions[activeCompetition].images.map((image, index) => (
                  <div
                    key={index}
                    className={`flex-shrink-0 w-[85vw] md:w-auto snap-center ${index === 0 ? 'md:col-span-2' : ''}`}
                  >
                    <div
                      className="rounded-lg overflow-hidden hover:shadow-card-hover transition-all duration-200 h-full"
                      style={{
                        borderColor: 'var(--color-border-light)',
                        borderWidth: '1px',
                        borderStyle: 'solid'
                      }}
                    >
                      <ImageCard
                        src={image}
                        alt={`${competitions[activeCompetition].name} - Photo ${index + 1}`}
                        aspectRatio={index === 0 ? 'aspect-video' : 'aspect-square'}
                        rounded={false}
                        shadow={false}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompetitionShowcaseSection;
